//
//  QaProgramChecklistInfoPopover.tsx
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module reveals a QA Program Checklist task's standing description.
//

import { ActionIcon, Popover, Text } from "@mantine/core";
import { useState, type FC } from "react";

import AppSettings from "src/AppSettings";
import Icon from "src/components/common/Icon";

/** Supplies the task whose standing description is revealed. */
export interface QaProgramChecklistInfoPopoverProps {
    task: string;
    additionalInfo: string;
}

/**
 * Reveals a task's standing description where there is no room to show it inline.
 *
 * Postconditions: Hover or keyboard focus opens the popover, matching the form-label precedent, so
 * the description is reachable without a pointer. The dropdown is portalled because the pinned
 * panel scrolls and would otherwise clip it.
 */
const QaProgramChecklistInfoPopover: FC<QaProgramChecklistInfoPopoverProps> = ({
    task,
    additionalInfo
}) => {
    const [focusedWithin, setFocusedWithin] = useState(false);
    const [hovered, setHovered] = useState(false);

    return (
        <Popover
            opened={hovered || focusedWithin}
            position="bottom-end"
            withinPortal
        >
            <Popover.Target>
                <ActionIcon
                    variant="subtle"
                    color="gray"
                    aria-label={`Additional information for ${task}`}
                    onFocusCapture={() => setFocusedWithin(true)}
                    onBlurCapture={() => setFocusedWithin(false)}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                >
                    <Icon icon={AppSettings.icons.information} />
                </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown bg="blue.1">
                <Text
                    size="sm"
                    style={{ whiteSpace: "pre-wrap" }}
                >
                    {additionalInfo}
                </Text>
            </Popover.Dropdown>
        </Popover>
    );
};

export default QaProgramChecklistInfoPopover;
